import { logger } from "@scott/logger";
import type { paths } from "@scott/scott-api";
import { memoizeRequest } from "@/lib/server/request-memo";
import { getShopperCommerceContext } from "../get-commerce-context";
import { getDetailCacheTags } from "./catalogue-cache-tags";
import { getProductFetchOptions } from "./product-fetch-options";
import { runCatalogueRequest } from "./run-catalogue-request";

export type ProductDetailProduct =
  paths["/commerce/v1/products/{id}"]["get"]["responses"][200]["content"]["application/json"]["data"];

export type ProductDetailVariant = NonNullable<
  ProductDetailProduct["variantProducts"]
>[number];

export interface GetProductDetailParams {
  country?: string;
  productCode: string;
  language?: string;
  suppressErrorLogging?: boolean;
  /** Signed-in shopper's OCC token, so SAP applies their own promotions. */
  shopperToken?: string;
  /** Tells the request stats a colour's article from a product. */
  kind?: "product" | "article";
  /** For an article: its base product, so a flush of the product reaches it. */
  baseProductCode?: string;
}

// The API answers an unknown code with a 400, so only its message tells
// "this product does not exist" apart from a real failure.
const PRODUCT_NOT_FOUND_MESSAGE = /^product with code .+ not found/i;

function isProductNotFound(error: unknown): boolean {
  const message =
    typeof error === "object" && error !== null && "message" in error
      ? error.message
      : undefined;

  return typeof message === "string" && PRODUCT_NOT_FOUND_MESSAGE.test(message);
}

/**
 * A successful fetch carries the product; a failed one carries the API error.
 * The memoized fetch returns this instead of logging, so a suppressing caller
 * (generateMetadata) and a logging one (the page) can share a single request
 * while each keeps its own logging decision.
 */
type ProductDetailResult =
  | { ok: true; product: ProductDetailProduct }
  | { ok: false; error: unknown };

export function getProductDetail(
  params: GetProductDetailParams
): Promise<ProductDetailProduct | undefined> {
  // Dedupe within a request by the fields that determine the response.
  // `suppressErrorLogging` is excluded from the key and from the memoized fetch:
  // logging is applied per caller below, so callers that disagree on it still
  // share one request without either silencing the other.
  const key = `product-detail:${params.productCode}:${params.country ?? ""}:${params.language ?? ""}:${params.shopperToken ?? ""}`;
  return memoizeRequest(key, () => fetchProductDetail(params)).then(
    (result) => {
      if (result.ok) {
        return result.product;
      }
      if (isProductNotFound(result.error)) {
        // Expected for codes that are not in this base store (e.g. a Contentful
        // entry or a search hit SAP no longer serves); callers skip the product.
        logger.debug("getProductDetail: product not found", {
          productCode: params.productCode,
        });
      } else if (!params.suppressErrorLogging) {
        logger.error("getProductDetail: request failed", {
          error: result.error,
          productCode: params.productCode,
        });
      }
    }
  );
}

async function fetchProductDetail(
  params: GetProductDetailParams
): Promise<ProductDetailResult> {
  const { scottClient, baseSiteId, isPersonalised } =
    await getShopperCommerceContext({
      country: params.country,
      shopperToken: params.shopperToken,
    });
  const cacheOptions = getProductFetchOptions({
    isPersonalised,
    tags: getDetailCacheTags({
      baseSiteId,
      language: params.language,
      productCode: params.baseProductCode ?? params.productCode,
    }),
  });

  const { data, error } = await runCatalogueRequest(
    { scottClient, isPersonalised },
    (client) =>
      client.GET("/commerce/v1/products/{id}", {
        params: {
          path: { id: params.productCode },
          query: {
            baseSiteId,
            fields: "B2BPORTAL_DEFAULT",
            lang: params.language,
          },
        },
        ...cacheOptions,
      }),
    params.kind ?? "product"
  );

  if (error || !data) {
    return { ok: false, error };
  }

  return { ok: true, product: data.data as ProductDetailProduct };
}
